import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useSuperuser } from "@/hooks/useSuperuser";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Settings, Trash2, Archive, Clock, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface CleanupResult {
  success: boolean;
  message: string;
  archived_count?: number;
  deleted_count?: number;
  cutoff_date?: string;
  retention_days?: number;
}

interface LastCleanup {
  created_at: string;
  details: {
    retention_days: number;
    cutoff_date: string;
    archived_count: number;
    deleted_count: number;
  };
}

export default function AdminLogRetentionSettings() {
  const { isSuperuser, loading: superuserLoading } = useSuperuser();
  const { settings: systemSettings } = useSystemSettings();
  const [retentionDays, setRetentionDays] = useState(365);
  const [archiveBeforeDelete, setArchiveBeforeDelete] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastCleanup, setLastCleanup] = useState<LastCleanup | null>(null);
  const [loadingStats, setLoadingStats] = useState(true);
  const [logsToClean, setLogsToClean] = useState(0);
  const [totalLogs, setTotalLogs] = useState(0);

  useEffect(() => {
    fetchStats();
  }, [retentionDays]);

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      // Get total logs count
      const { count: total } = await supabase.from("admin_logs").select("*", { count: "exact", head: true });

      setTotalLogs(total || 0);

      // Get count of logs older than retention period
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - retentionDays);

      const { count: toClean } = await supabase
        .from("admin_logs")
        .select("*", { count: "exact", head: true })
        .lt("created_at", cutoffDate.toISOString());

      setLogsToClean(toClean || 0);

      // Get last cleanup action
      const { data: lastCleanupData } = await supabase
        .from("admin_logs")
        .select("created_at, details")
        .eq("action", "log_cleanup")
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (lastCleanupData) {
        setLastCleanup(lastCleanupData as LastCleanup);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  const runCleanup = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("cleanup-logs", {
        body: {
          retention_days: retentionDays,
          archive_before_delete: archiveBeforeDelete,
        },
      });

      if (error) throw error;

      const result = data as CleanupResult;

      if (result.success) {
        toast.success(
          `Cleanup complete: ${result.archived_count || 0} logs archived, ${result.deleted_count || 0} logs deleted`,
        );
        fetchStats();
      } else {
        toast.info(result.message || "No logs to clean up");
      }
    } catch (error: any) {
      console.error("Cleanup error:", error);
      toast.error(error.message || "Failed to run cleanup");
    } finally {
      setRunning(false);
    }
  };

  const getCutoffDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - retentionDays);
    return date;
  };

  if (!isSuperuser && !superuserLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Log Retention Settings</h1>
          <p className="text-muted-foreground">Only superusers can access log retention settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Log Retention Settings</h1>
        <p className="text-muted-foreground">Configure how long logs are kept and manage cleanup</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Current Stats Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              Current Status
            </CardTitle>
            <CardDescription>Overview of your log storage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingStats ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Total Logs</p>
                    <p className="text-2xl font-bold">{totalLogs.toLocaleString()}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">Logs to Archive</p>
                    <p className="text-2xl font-bold text-amber-600">{logsToClean.toLocaleString()}</p>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Cutoff Date (based on {retentionDays} days)</p>
                  <Badge variant="outline" className="font-mono">
                    {format(getCutoffDate(), "MMMM d, yyyy")}
                  </Badge>
                  <p className="text-xs text-muted-foreground">Logs before this date will be archived and deleted</p>
                </div>

                {lastCleanup && (
                  <>
                    <Separator />
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">Last Cleanup</p>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        <span className="text-sm">{format(new Date(lastCleanup.created_at), "MMM d, yyyy HH:mm")}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Archived {lastCleanup.details.archived_count} logs, deleted {lastCleanup.details.deleted_count}{" "}
                        logs
                      </p>
                    </div>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Retention Configuration
            </CardTitle>
            <CardDescription>Configure log retention policy</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="retention-days" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Retention Period (days)
              </Label>
              <Input
                id="retention-days"
                type="number"
                min={7}
                max={365}
                value={retentionDays}
                onChange={(e) => setRetentionDays(Math.max(7, Math.min(365, parseInt(e.target.value) || 90)))}
              />
              <p className="text-xs text-muted-foreground">
                Logs older than this will be archived and deleted. Minimum 7 days, maximum 365 days.
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="archive-toggle" className="flex items-center gap-2">
                  <Archive className="h-4 w-4" />
                  Archive Before Delete
                </Label>
                <p className="text-xs text-muted-foreground">Save logs to backup storage before deletion</p>
              </div>
              <Switch id="archive-toggle" checked={archiveBeforeDelete} onCheckedChange={setArchiveBeforeDelete} />
            </div>

            <Separator />

            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-600">Scheduled Cleanup</p>
                  <p className="text-muted-foreground">
                    Automatic cleanup runs every Sunday at 3:00 AM with 90-day retention.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Manual Cleanup Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5" />
            Manual Cleanup
          </CardTitle>
          <CardDescription>Manually trigger log cleanup with your configured settings</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {logsToClean > 0 ? (
                  <>
                    <span className="text-amber-600">{logsToClean.toLocaleString()}</span> logs will be{" "}
                    {archiveBeforeDelete ? "archived and " : ""}deleted
                  </>
                ) : (
                  "No logs to clean up based on current settings"
                )}
              </p>
              <p className="text-xs text-muted-foreground">This will apply the retention settings configured above</p>
            </div>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant={logsToClean > 0 ? "destructive" : "outline"} disabled={running || logsToClean === 0 || !systemSettings.log_cleanup_enabled}>
                  {running ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Running Cleanup...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Run Cleanup Now
                    </>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm Log Cleanup</AlertDialogTitle>
                  <AlertDialogDescription className="space-y-2">
                    <p>
                      This will {archiveBeforeDelete ? "archive and " : ""}delete{" "}
                      <strong>{logsToClean.toLocaleString()}</strong> logs older than{" "}
                      <strong>{retentionDays} days</strong>.
                    </p>
                    {archiveBeforeDelete ? (
                      <p className="text-green-600">✓ Logs will be saved to backup storage before deletion</p>
                    ) : (
                      <p className="text-destructive">⚠ Logs will be permanently deleted without backup</p>
                    )}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={runCleanup}>Confirm Cleanup</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Retention Presets */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Presets</CardTitle>
          <CardDescription>Common retention period configurations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[
              { days: 30, label: "30 Days" },
              { days: 60, label: "60 Days" },
              { days: 90, label: "90 Days (Default)" },
              { days: 180, label: "6 Months" },
              { days: 365, label: "1 Year" },
            ].map((preset) => (
              <Button
                key={preset.days}
                variant={retentionDays === preset.days ? "default" : "outline"}
                size="sm"
                onClick={() => setRetentionDays(preset.days)}
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
